import { defineStore, storeToRefs } from "pinia";
import { shallowRef, watch } from "vue";
import { useHabilitationsAdminEngineStore, type Entite } from './useHabilitationsAdminEngineStore'
import {
  HabilitationAddErreurDoublon,
  type ContactDTO,
  type HabilitationAddDTO,
  type HabilitationDTO,
  type HabilitationDemandeDTO,
} from '@/features/api/habilitation/dto'
import { useImmerFieldPatcher } from '@/features/interface/composables'
import {
  type NiveauPermission,
  type TypePermission,
  TypePublic,
} from '@/features/domaine/types/opco'
import { isNullOrUndefined } from '@/features/shared/tools/object'

export const useHabilitationsAdminContentStore = defineStore('habilitations-admin-v2-content', () => {
  const habilitationsEffectives = shallowRef<HabilitationDTO[]>()
  const habilitationsDemande = shallowRef<HabilitationDemandeDTO[]>()

  const engine = useHabilitationsAdminEngineStore()
  const { isReady, isReadOnly, identifiantInterneApi, engineState, estModeEntiteSirenSiret, dataToken, allowNavigate } = storeToRefs(engine)

  async function loadHabilitations() {
    habilitationsEffectives.value = undefined
    habilitationsDemande.value = undefined
    if (!isReady.value) {
      return
    }
    if (engineState.value.type === 'administrateur') {
      // Chargement Habilitations à administrer
      const service = engine.getService()
      habilitationsEffectives.value = (await service.getHabilitations(identifiantInterneApi.value)) ?? []
    } else {
      // Chargement MesHabilitations
      const service = engine.getService()
      const promesseHabilitationsValidées = service.getHabilitations(identifiantInterneApi.value)
      const promesseDemandesEnAttente = service.getDemandesEnAttente()

      const [habilitationsValidées, habilitationsEnAttente] = await Promise.all([
        promesseHabilitationsValidées,
        promesseDemandesEnAttente,
      ])
      habilitationsEffectives.value = habilitationsValidées ?? []
      habilitationsDemande.value = habilitationsEnAttente ?? []
    }
  }

  const { setupCustomUpdater } = useImmerFieldPatcher<{ habilitations: HabilitationDTO[] | undefined }>({
    getModel: () => ({ habilitations: habilitationsEffectives.value }),
    async setModel(newModel, patchs) {
      // console.log("patch", patchs)
      const service = engine.getService()

      let firstPatchIndex: number | undefined = undefined
      for (const patch of patchs) {
        if (patch.op === 'replace' && ['permissions', 'statut', 'publics'].includes(`${ patch.path[3] ?? '' }`)) {
          // Modification habilitations en bloc
          // Ex : Quand plusieurs patch sur une seule habilitation, une seule maj via api
          const index = patch.path[1] as number
          if (firstPatchIndex !== index) {
            firstPatchIndex = index
            const habilitationToUpdate = newModel.habilitations![index]!
            const habilitationUpdated = (await service.updateHabilitationPermissions(habilitationToUpdate))!
            const habilitations = [...newModel.habilitations!]
            habilitations[index] = habilitationUpdated
            newModel = { habilitations }
          }
        } else if (patch.path[0] === 'habilitations') {
          if (isNullOrUndefined(firstPatchIndex) && patch.op === 'replace') {
            // Capture les déplacements d'habilitations
            firstPatchIndex = patch.path[1] as number
          } else if (patch.op === 'remove') {
            // Suppression habilitation
            const index = firstPatchIndex ?? (patch.path[1] as number)
            firstPatchIndex = undefined

            const habilitation = habilitationsEffectives.value!.at(index)!
            await service.deleteHabilitation(habilitation)
          }
        }
      }
      habilitationsEffectives.value = newModel.habilitations
    },
  })

  async function addHabilitation(value: Entite | ContactDTO): Promise<HabilitationDTO | undefined> {
    const service = engine.getService()

    const commonModel: Pick<HabilitationAddDTO, 'typeEntreprise' | 'publics' | 'statut'> = {
      typeEntreprise: engine.engineState.context === 'adherent' ? "Adherent" : "Prestataire",
      publics: [{ type: 'Permanent' }],
      statut: 'Membre',
    }

    const entiteModel: HabilitationAddDTO = ('email' in value ? {
      identifiantEntiteJuridique: engine.identifiantInterneApi!,
      typeEntiteJuridique: engine.identifiantInterneApi!.length === 9 ? "Entreprise" : "Etablissement",
      userId: value.userId ?? value.email.toLocaleLowerCase(),
      ...commonModel
    } : {
      identifiantEntiteJuridique: value.identifiant,
      typeEntiteJuridique: value.identifiant.length === 9 ? "Entreprise" : "Etablissement",
      userId: engine.identifiantInterneApi!,
      ...commonModel
    })

    try {
      const habilitation = await service.addHabilitation(entiteModel, { dialogMode: 'retry' })
      await setupCustomUpdater<HabilitationDTO>((patch, habilitation) => {
        patch.habilitations?.push(habilitation)
      })(habilitation)

      return habilitation
    } catch (err) {
      if (err instanceof HabilitationAddErreurDoublon) {
        // Il faut recharger les habilitations
        await loadHabilitations()
      }
    }
  }

  const setHabilitationMembre = setupCustomUpdater<HabilitationDTO>((patch, habilitation) => {
    const habititationsPatch = patch.habilitations?.find((h) => h.id === habilitation.id)
    if (habititationsPatch?.autorisations.statut !== 'Visiteur') return
    habititationsPatch.autorisations.statut = "Membre"
  })

  const setTypePublic = setupCustomUpdater<{
    habilitation: HabilitationDTO,
    publics:  { type: TypePublic }[]
  }>((patch, options) => {
    const habititationsPatch = patch.habilitations!.find(h => h.id === options.habilitation.id)!
    if (habititationsPatch?.autorisations.statut === 'Visiteur') return
    if (engineState.value.context === 'prestataire') return
    habititationsPatch.autorisations.publics = [...options.publics]
  })

  const setNiveauPermission = setupCustomUpdater<{
    habilitation: HabilitationDTO
    type: TypePermission
    niveau: NiveauPermission
  }>((patch, options) => {
    const habititationsPatch = patch.habilitations?.find((h) => h.id === options.habilitation.id)
    const permission = habititationsPatch?.autorisations?.permissions?.find((h) => h.type === options.type)
    if (!permission) return

    // Mise en cohérence du statut d'autorisation avec les permissions
    if (permission.type === "Administration") {
      // Sécurité : empéche un admin de se retirer les droits
      // d'admin à lui meme
      if (habititationsPatch?.userId === engine.dataToken?.userId) return

      if (options.niveau === 'Ecriture') {
        habititationsPatch!.autorisations.statut = "Administrateur"
      } else {
        habititationsPatch!.autorisations.statut = "Membre"
      }
    }

    // Synchronisation DR avec Facture
    /* Ca c'est n'importe quoi ! À revoir !! */
    if (permission.type === "Facture") {
      const drPermission = habititationsPatch?.autorisations?.permissions?.find((h) => h.type === 'DR')
      if (drPermission) {
        drPermission.niveau = options.niveau
      }
    }

    permission.niveau = options.niveau
  })

  const deleteHabilitation = setupCustomUpdater<HabilitationDTO>((patch, habilitation) => {
    const index = patch.habilitations?.findIndex(h => h.id === habilitation.id);
    if (index === undefined) return
    patch.habilitations?.splice(index, 1)
  })

  // Observe le rechargement des habilitations
  watch(
    () => ([isReady.value, engineState.value.type, identifiantInterneApi.value]),
    loadHabilitations,
    { immediate: true, deep: true }
  )

  return {
    isReadOnly,
    addHabilitation,
    setHabilitationMembre,
    setNiveauPermission,
    setTypePublic,
    deleteHabilitation,
    dataToken,
    engineState,
    estModeEntiteSirenSiret,
    habilitationsEffectives,
    habilitationsDemande,
    searchContact: engine.searchContact,
    searchEntite: engine.searchEntite,
    allowNavigate,
    navigateTo: engine.navigateTo,
  }
})
